import { useState } from "react";

const faqs = [
  {
    category: "Buying Crypto",
    question: "How do I buy crypto with Naira?",
    answer:
      "Go to the Buy page, select your preferred crypto asset, enter the Naira amount you want to spend, then complete payment via Paystack. Your crypto will be credited within minutes after payment confirmation.",
  },
  {
    category: "Buying Crypto",
    question: "What is the minimum buy amount?",
    answer:
      "The minimum purchase amount is ₦1,000. There is no maximum limit, but transactions above ₦5,000,000 may require additional verification.",
  },
  {
    category: "Selling Crypto",
    question: "How long does a Naira withdrawal take?",
    answer:
      "Once you confirm a sell order, the Naira equivalent is sent to your linked bank account within 5–30 minutes on business days. Bank processing times may vary.",
  },
  {
    category: "Deposits & Withdrawals",
    question: "How do I deposit crypto into my wallet?",
    answer:
      "Navigate to the Deposit page, select the crypto asset, copy your unique wallet address or scan the QR code, then send from your external wallet. Deposits are credited after the required network confirmations.",
  },
  {
    category: "Deposits & Withdrawals",
    question: "How do I withdraw crypto to an external wallet?",
    answer:
      "Go to Withdraw Crypto, select the asset, enter the destination wallet address and amount, then confirm. Withdrawals are processed within 10–30 minutes depending on network congestion.",
  },
  {
    category: "Account & KYC",
    question: "Why do I need to complete KYC?",
    answer:
      "KYC (Know Your Customer) is required by Nigerian financial regulations. It helps us protect your account, prevent fraud, and comply with CBN and SEC guidelines.",
  },
  {
    category: "Account & KYC",
    question: "My KYC is pending for too long. What do I do?",
    answer:
      "KYC verification typically takes 1–24 hours. If it has been longer than 24 hours, please contact our support team with your registered email and we will resolve it promptly.",
  },
  {
    category: "Fees",
    question: "What fees does NairaX charge?",
    answer:
      "NairaX charges a 1.5% fee on buy/sell transactions and a flat ₦50 fee on Naira withdrawals. Crypto withdrawal fees vary by network (e.g., BTC, ETH gas fees).",
  },
];

const categories = Array.from(new Set(faqs.map((f) => f.category)));

const quickLinks = [
  { icon: "ri-whatsapp-line", label: "WhatsApp Support", sub: "Chat instantly", color: "#25D366", href: "https://wa.me/2348012345678" },
  { icon: "ri-mail-send-line", label: "Email Support", sub: "support@nairax.ng", color: "#059669", href: "mailto:support@nairax.ng" },
  { icon: "ri-twitter-x-line", label: "Twitter / X", sub: "@NairaX_official", color: "#0F172A", href: "https://twitter.com/NairaXofficial" },
];

export default function SupportModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("help");
  const [openFaq, setOpenFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Buying Crypto");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const filteredFaqs = faqs.filter((f) => f.category === activeCategory);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.message.length > 500) {
      setError("Message must be 500 characters or less.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d7bv5d6lfkej60p1k900", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl w-full max-h-[92vh] flex flex-col"
        style={{ animation: "slideUp 0.3s ease-out" }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-slate-600 text-lg" />
            </button>
            <div>
              <h2 className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Help &amp; Support
              </h2>
              <p className="text-slate-400 text-xs">We&apos;re here to help you</p>
            </div>
          </div>
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <i className="ri-customer-service-2-line text-amber-500 text-lg" />
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="px-5 pt-4 pb-3 flex-shrink-0">
          <div className="flex bg-slate-100 rounded-full p-1 gap-1">
            {(["help", "contact"]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"
                }`}
              >
                {tab === "help" ? "FAQ" : "Contact Us"}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 pb-8">
          {/* ── FAQ Tab ── */}
          {activeTab === "help" && (
            <div>
              {/* Quick contact links */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {quickLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl py-4 px-2 hover:bg-slate-100 transition-colors cursor-pointer"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${link.color}15` }}
                    >
                      <i className={`${link.icon} text-lg`} style={{ color: link.color }} />
                    </div>
                    <p className="text-slate-700 text-xs font-semibold text-center leading-tight">{link.label}</p>
                  </a>
                ))}
              </div>

              {/* Category tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setActiveCategory(cat); setOpenFaq(null); }}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* FAQ accordion */}
              <div className="divide-y divide-slate-100 bg-slate-50 rounded-2xl overflow-hidden">
                {filteredFaqs.map((faq, idx) => (
                  <div key={idx}>
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-start gap-3 px-4 py-4 text-left cursor-pointer hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-question-line text-emerald-600 text-xs" />
                      </div>
                      <p className="flex-1 text-slate-700 text-sm font-medium leading-snug">{faq.question}</p>
                      <i
                        className={`ri-arrow-down-s-line text-slate-300 text-lg flex-shrink-0 transition-transform mt-0.5 ${
                          openFaq === idx ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openFaq === idx && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="ml-9 bg-white border border-slate-100 rounded-xl px-4 py-3">
                          <p className="text-slate-500 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-slate-400 text-xs text-center mt-5">
                Can&apos;t find your answer?{" "}
                <button
                  onClick={() => setActiveTab("contact")}
                  className="text-emerald-600 font-semibold cursor-pointer hover:underline"
                >
                  Contact us directly
                </button>
              </p>
            </div>
          )}

          {/* ── Contact Tab ── */}
          {activeTab === "contact" && (
            <div>
              {/* Response time banner */}
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-emerald-600 text-base" />
                </div>
                <div>
                  <p className="text-emerald-700 text-sm font-semibold">Average response time: &lt; 2 hours</p>
                  <p className="text-emerald-600 text-xs">Mon–Fri, 8am–8pm WAT</p>
                </div>
              </div>

              {submitted ? (
                <div className="flex flex-col items-center gap-4 py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-3xl" />
                  </div>
                  <div className="text-center">
                    <p className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Message Sent!
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      We&apos;ve received your message and will get back to you within 2 hours.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="bg-emerald-600 text-white text-sm font-semibold px-6 py-3 rounded-xl cursor-pointer hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0" />
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="text-slate-500 text-xs font-medium mb-1.5 block">Full Name</label>
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-emerald-400 transition-colors">
                      <i className="ri-user-3-line text-slate-300 text-base flex-shrink-0" />
                      <input
                        type="text"
                        name="name"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="flex-1 bg-transparent outline-none text-slate-700 text-sm placeholder:text-slate-300"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-slate-500 text-xs font-medium mb-1.5 block">Email Address</label>
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-emerald-400 transition-colors">
                      <i className="ri-mail-line text-slate-300 text-base flex-shrink-0" />
                      <input
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="flex-1 bg-transparent outline-none text-slate-700 text-sm placeholder:text-slate-300"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="text-slate-500 text-xs font-medium mb-1.5 block">Subject</label>
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-emerald-400 transition-colors">
                      <i className="ri-chat-1-line text-slate-300 text-base flex-shrink-0" />
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        className="flex-1 bg-transparent outline-none text-slate-700 text-sm cursor-pointer"
                      >
                        <option value="">Select a topic</option>
                        <option value="Buy/Sell Issue">Buy / Sell Issue</option>
                        <option value="Deposit Not Credited">Deposit Not Credited</option>
                        <option value="Withdrawal Delayed">Withdrawal Delayed</option>
                        <option value="KYC Verification">KYC Verification</option>
                        <option value="Account Security">Account Security</option>
                        <option value="Fees & Charges">Fees &amp; Charges</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-slate-500 text-xs font-medium">Message</label>
                      <span className={`text-xs ${form.message.length > 450 ? "text-red-400" : "text-slate-300"}`}>
                        {form.message.length}/500
                      </span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-emerald-400 transition-colors">
                      <textarea
                        name="message"
                        placeholder="Describe your issue in detail..."
                        value={form.message}
                        maxLength={500}
                        rows={4}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full bg-transparent outline-none text-slate-700 text-sm placeholder:text-slate-300 resize-none"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-emerald-600 text-white text-sm font-bold py-4 rounded-xl cursor-pointer hover:bg-emerald-700 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-2-line text-base" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Quick links at bottom */}
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="text-slate-400 text-xs text-center mb-3">Or reach us directly</p>
                <div className="flex flex-col gap-2">
                  {quickLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${link.color}15` }}
                      >
                        <i className={`${link.icon} text-base`} style={{ color: link.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-700 text-sm font-medium">{link.label}</p>
                        <p className="text-slate-400 text-xs">{link.sub}</p>
                      </div>
                      <i className="ri-external-link-line text-slate-300 text-base" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
